import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import axios from 'axios';
import { 
  HospitalNotification, NotificationFilter, DashboardNotificationStats,
  HospitalNotificationType, NotificationPriority, HospitalDepartment,
  NotificationContextState, NotificationContextActions, HospitalNotificationResponse
} from '../types/hospital-notification';
import { SocketConnectionStatus } from '../types/notification';
import { hospitalSocketService } from '../services/socketService';

// API 기본 URL (환경변수 우선)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.219.106:4000/api';

// 초기 필터 상태
const initialFilter: NotificationFilter = {
  types: Object.values(HospitalNotificationType),
  departments: Object.values(HospitalDepartment),
  priorities: Object.values(NotificationPriority),
  categories: ['application', 'shift', 'staff', 'compliance', 'system'],
  showUnreadOnly: false
};

// 초기 통계 상태
const initialStats: DashboardNotificationStats = {
  total: 0, unread: 0, urgent: 0,
  byCategory: { application: 0, shift: 0, staff: 0, compliance: 0, system: 0 },
  byDepartment: Object.values(HospitalDepartment).reduce((acc, dept) => ({
    ...acc, [dept]: 0
  }), {} as Record<HospitalDepartment, number>),
  byPriority: Object.values(NotificationPriority).reduce((acc, priority) => ({
    ...acc, [priority]: 0
  }), {} as Record<NotificationPriority, number>)
};

// 초기 상태
const initialState: NotificationContextState = {
  notifications: [], filteredNotifications: [], stats: initialStats,
  filter: initialFilter, isLoading: false, error: null,
  connectionStatus: { isConnected: false, isAuthenticated: false }
};

// Reducer 액션 타입
type NotificationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_NOTIFICATIONS'; payload: { notifications: HospitalNotification[]; stats: DashboardNotificationStats } }
  | { type: 'ADD_NOTIFICATION'; payload: HospitalNotification }
  | { type: 'UPDATE_NOTIFICATION'; payload: { id: string; updates: Partial<HospitalNotification> } }
  | { type: 'SET_FILTER'; payload: Partial<NotificationFilter> }
  | { type: 'SET_CONNECTION_STATUS'; payload: SocketConnectionStatus };// 알림 필터링 함수
function filterNotifications(notifications: HospitalNotification[], filter: NotificationFilter): HospitalNotification[] {
  return notifications.filter(notification => {
    // 타입 필터
    if (!filter.types.includes(notification.type as unknown as HospitalNotificationType)) return false;
    
    // 카테고리 필터
    if (!filter.categories.includes(notification.category)) return false;
    
    // 부서 필터 (부서가 있는 경우만)
    if (notification.department && !filter.departments.includes(notification.department)) return false;
    
    // 우선순위 필터 (우선순위가 있는 경우만)
    if (notification.priority && !filter.priorities.includes(notification.priority)) return false;
    
    // 읽지 않은 알림만 표시
    if (filter.showUnreadOnly && notification.isRead) return false;
    
    // 날짜 범위 필터
    if (filter.dateRange) {
      const notificationDate = new Date(notification.createdAt);
      const startDate = new Date(filter.dateRange.start);
      const endDate = new Date(filter.dateRange.end);
      if (notificationDate < startDate || notificationDate > endDate) return false;
    }
    
    return true;
  });
}

// 통계 계산 함수
function calculateStats(notifications: HospitalNotification[]): DashboardNotificationStats {
  const stats: DashboardNotificationStats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.isRead).length,
    urgent: notifications.filter(n => n.priority === NotificationPriority.URGENT).length,
    byCategory: { application: 0, shift: 0, staff: 0, compliance: 0, system: 0 },
    byDepartment: Object.values(HospitalDepartment).reduce((acc, dept) => ({
      ...acc, [dept]: 0
    }), {} as Record<HospitalDepartment, number>),
    byPriority: Object.values(NotificationPriority).reduce((acc, priority) => ({
      ...acc, [priority]: 0
    }), {} as Record<NotificationPriority, number>)
  };

  notifications.forEach(notification => {
    // 카테고리별 카운트
    stats.byCategory[notification.category as keyof typeof stats.byCategory]++;
    
    // 부서별 카운트
    if (notification.department) {
      stats.byDepartment[notification.department]++;
    }
    
    // 우선순위별 카운트
    if (notification.priority) {
      stats.byPriority[notification.priority]++;
    }
  });

  return stats;
}// Reducer 함수
function notificationReducer(state: NotificationContextState, action: NotificationAction): NotificationContextState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };
      
    case 'SET_NOTIFICATIONS':
      const filteredNotifications = filterNotifications(action.payload.notifications, state.filter);
      return {
        ...state,
        notifications: action.payload.notifications,
        filteredNotifications,
        stats: action.payload.stats,
        error: null
      };
      
    case 'ADD_NOTIFICATION':
      const newNotifications = [action.payload, ...state.notifications];
      const newFilteredNotifications = filterNotifications(newNotifications, state.filter);
      const newStats = calculateStats(newNotifications);
      return {
        ...state,
        notifications: newNotifications,
        filteredNotifications: newFilteredNotifications,
        stats: newStats
      };
      
    case 'UPDATE_NOTIFICATION':
      const updatedNotifications = state.notifications.map(notification =>
        notification.id === action.payload.id
          ? { ...notification, ...action.payload.updates }
          : notification
      );
      const updatedFilteredNotifications = filterNotifications(updatedNotifications, state.filter);
      const updatedStats = calculateStats(updatedNotifications);
      return {
        ...state,
        notifications: updatedNotifications,
        filteredNotifications: updatedFilteredNotifications,
        stats: updatedStats
      };
      
    case 'SET_FILTER':
      const newFilter = { ...state.filter, ...action.payload };
      const reFilteredNotifications = filterNotifications(state.notifications, newFilter);
      return {
        ...state,
        filter: newFilter,
        filteredNotifications: reFilteredNotifications
      };
      
    case 'SET_CONNECTION_STATUS':
      return { ...state, connectionStatus: action.payload };
      
    default:
      return state;
  }
}// Context 생성
const NotificationContext = createContext<(NotificationContextState & NotificationContextActions) | null>(null);

// Provider 컴포넌트
interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  // API 호출을 위한 헤더 생성
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  // 알림 목록 새로고침
  const refreshNotifications = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      console.log('🏥 [NotificationContext] Fetching hospital notifications...');
      
      const response = await axios.get<HospitalNotificationResponse>(
        `${API_BASE_URL}/notifications/hospital`,
        {
          headers: getAuthHeaders(),
          params: {
            limit: 100,
            includeStats: true
          }
        }
      );

      const data = response.data;
      console.log('🏥 [NotificationContext] Notifications fetched:', data.notifications?.length || 0);
      
      dispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          notifications: data.notifications || [],
          stats: data.stats || initialStats
        }
      });
    } catch (error: any) {
      console.error('🏥 [NotificationContext] Error fetching notifications:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to fetch notifications' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [getAuthHeaders]);  // 알림 읽음 처리
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // 즉시 UI 업데이트 (낙관적 업데이트)
      dispatch({
        type: 'UPDATE_NOTIFICATION',
        payload: { id: notificationId, updates: { isRead: true } }
      });

      // Socket을 통해 읽음 처리
      hospitalSocketService.markNotificationRead(notificationId);

      // API를 통해서도 읽음 처리
      await axios.patch(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {},
        { headers: getAuthHeaders() }
      );

      console.log('🏥 [NotificationContext] Notification marked as read:', notificationId);
    } catch (error: any) {
      console.error('🏥 [NotificationContext] Error marking notification as read:', error);
      
      // 실패 시 UI 원복
      dispatch({
        type: 'UPDATE_NOTIFICATION',
        payload: { id: notificationId, updates: { isRead: false } }
      });
      
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to mark notification as read' });
    }
  }, [getAuthHeaders]);

  // 모든 알림 읽음 처리
  const markAllAsRead = useCallback(async () => {
    try {
      // 즉시 UI 업데이트
      const updatedNotifications = state.notifications.map(n => ({ ...n, isRead: true }));
      dispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          notifications: updatedNotifications,
          stats: calculateStats(updatedNotifications)
        }
      });

      // API 호출
      await axios.patch(
        `${API_BASE_URL}/notifications/mark-all-read`,
        {},
        { headers: getAuthHeaders() }
      );

      console.log('🏥 [NotificationContext] All notifications marked as read');
    } catch (error: any) {
      console.error('🏥 [NotificationContext] Error marking all notifications as read:', error);
      
      // 실패 시 새로고침으로 원복
      await refreshNotifications();
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to mark all notifications as read' });
    }
  }, [getAuthHeaders, refreshNotifications, state.notifications]);  // 필터 설정
  const setFilter = useCallback((newFilter: Partial<NotificationFilter>) => {
    dispatch({ type: 'SET_FILTER', payload: newFilter });
  }, []);

  // Socket 연결
  const connectSocket = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No access token available');
      }

      console.log('🏥 [NotificationContext] Connecting socket...');
      const success = await hospitalSocketService.connect(token);
      const status = hospitalSocketService.getConnectionStatus();
      
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: status });
      
      if (success) {
        console.log('🏥 [NotificationContext] Socket connected successfully');
        await refreshNotifications();
      } else {
        console.error('🏥 [NotificationContext] Socket connection failed');
      }
    } catch (error: any) {
      console.error('🏥 [NotificationContext] Socket connection error:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Socket connection error' });
    }
  }, [refreshNotifications]);

  // Socket 해제
  const disconnectSocket = useCallback(() => {
    console.log('🏥 [NotificationContext] Disconnecting socket...');
    hospitalSocketService.disconnect();
    dispatch({ 
      type: 'SET_CONNECTION_STATUS', 
      payload: { isConnected: false, isAuthenticated: false } 
    });
  }, []);

  // 실시간 알림 수신 핸들러
  const handleNewNotification = useCallback((notification: any) => {
    console.log('🏥 [NotificationContext] New notification received:', notification);
    // 기본 알림을 병원 알림으로 변환
    const hospitalNotification: HospitalNotification = {
      ...notification,
      category: notification.category || 'system',
      priority: notification.priority || NotificationPriority.MEDIUM
    };
    dispatch({ type: 'ADD_NOTIFICATION', payload: hospitalNotification });
  }, []);

  // Socket 이벤트 리스너 설정
  useEffect(() => {
    hospitalSocketService.onNotification(handleNewNotification);
    
    // 연결 상태 주기적 확인 (30초마다)
    const statusInterval = setInterval(() => {
      const status = hospitalSocketService.getConnectionStatus();
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: status });
    }, 30000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [handleNewNotification]);  // Context 값 생성
  const contextValue = {
    ...state,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    setFilter,
    connectSocket,
    disconnectSocket
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Context 사용을 위한 커스텀 훅
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;